<template>
  <div class="k-cal">
    <!-- Header -->
    <div class="k-cal-header">
      <v-btn icon small @click="prevMonth">
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>

      <div class="k-cal-title">
        {{ monthLabel }}
      </div>

      <v-btn icon small @click="nextMonth">
        <v-icon>mdi-chevron-right</v-icon>
      </v-btn>
    </div>

    <!-- Weekdays -->
    <div class="k-weekdays">
      <div v-for="(w, idx) in weekdays" :key="`${w}-${idx}`" class="k-weekday">{{ w }}</div>
    </div>

    <!-- Grid days -->
    <div class="k-grid">
      <div
        v-for="cell in cells"
        :key="cell.key"
        class="k-cell"
        :class="{
          'is-outside': !cell.inMonth,
          'is-today': cell.isToday,
          'is-selected': cell.ymd === selectedYMD
        }"
        @click="onCellClick(cell)"
      >
        <div class="k-cell-top">
          <span class="k-day-num">{{ cell.day }}</span>
        </div>

        <div class="k-cell-events" v-if="cell.events.length">
          <div
            v-for="(e, idx) in cell.events.slice(0, maxPerDayResponsive)"
            :key="eventKey(e, idx, cell.ymd)"
            class="k-pill"
            :class="pillClass(e)"
            :style="pillStyle(e)"
            :title="tooltipForEvent(e)"
            @click.stop="onEventClick(cell, e)"
          >
            <v-icon
              v-if="hasGroups(e)"
              class="k-pill-group-icon"
            >
              mdi-account-group
            </v-icon>
            <span class="k-pill-text">{{ e.title || e.name }}</span>
          </div>

          <div
            v-if="cell.events.length > maxPerDayResponsive"
            class="k-more"
            @click.stop="onCellClick(cell)"
          >
            +{{ cell.events.length - maxPerDayResponsive }}
          </div>
        </div>
      </div>
    </div>
<!--
    <div class="k-actions">
      <v-btn small text @click="goToday">Hoy</v-btn>
    </div>
    -->
  </div>
</template>

<script>
// ---------- Date helpers ----------
function pad2(n) {
  return String(n).padStart(2, "0");
}
function toYMDLocal(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
function addDays(d, n) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}
function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function addMonths(d, n) {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}
function toDate(ts) {
  if (ts?.toDate) return ts.toDate();
  if (ts?.seconds) return new Date(ts.seconds * 1000);
  return new Date(ts);
}
// Monday-first offset: Monday=0 ... Sunday=6
function mondayIndex(jsDay /* 0=Sun..6=Sat */) {
  return (jsDay + 6) % 7;
}

export default {
  name: "CalendarView",
  props: {
    events: { type: Array, required: true },
    groups: { type: Array, required: true },
    uid: { type: String, default: "" },
    maxPerDay: { type: Number, default: 4 }
  },

  data() {
    return {
      cursor: startOfMonth(new Date()),
      selectedYMD: null,
      weekdayLabels: {
        es: ["L", "M", "X", "J", "V", "S", "D"],
        en: ["M", "T", "W", "T", "F", "S", "S"],
        pt: ["S", "T", "Q", "Q", "S", "S", "D"]
      }
    };
  },

  computed: {
    weekdays() {
      return this.weekdayLabels[this.$i18n.locale] || this.weekdayLabels.es;
    },

    monthLabel() {
      return this.cursor.toLocaleDateString(this.$i18n.locale, { month: "long", year: "numeric" });
    },

    isMobile() {
      return this.$vuetify?.breakpoint?.smAndDown;
    },

    // CLAVE: en móvil mostramos 1 evento, pero con 2 líneas (se lee mucho más)
    maxPerDayResponsive() {
      return this.isMobile ? 2 : this.maxPerDay;
    },

    eventsByDay() {
      const map = Object.create(null);

      (this.events || []).forEach((e) => {
        if (!e?.startAt) return;
        const ymd = toYMDLocal(toDate(e.startAt));
        if (!map[ymd]) map[ymd] = [];
        map[ymd].push(e);
      });

      Object.keys(map).forEach((k) => {
        map[k].sort((a, b) => toDate(a.startAt).getTime() - toDate(b.startAt).getTime());
      });

      return map;
    },

    cells() {
      const first = startOfMonth(this.cursor);
      const offset = mondayIndex(first.getDay());
      const gridStart = addDays(first, -offset);

      const todayYMD = toYMDLocal(new Date());
      const month = this.cursor.getMonth();
      const year = this.cursor.getFullYear();

      const out = [];
      for (let i = 0; i < 42; i++) {
        const d = addDays(gridStart, i);
        const ymd = toYMDLocal(d);
        const inMonth = d.getMonth() === month && d.getFullYear() === year;

        out.push({
          key: ymd,
          ymd,
          date: d,
          day: d.getDate(),
          inMonth,
          isToday: ymd === todayYMD,
          events: this.eventsByDay[ymd] || []
        });
      }
      return out;
    }
  },

  methods: {
    prevMonth() {
      this.cursor = startOfMonth(addMonths(this.cursor, -1));
    },
    nextMonth() {
      this.cursor = startOfMonth(addMonths(this.cursor, 1));
    },
    goToday() {
      this.cursor = startOfMonth(new Date());
      const ymd = toYMDLocal(new Date());
      this.selectedYMD = ymd;
      this.$emit("day-click", ymd);
    },

    onCellClick(cell) {
      this.selectedYMD = cell.ymd;
      this.$emit("day-click", cell.ymd);
    },

    onEventClick(cell, event) {
      this.selectedYMD = cell.ymd;
      this.$emit("event-click", { ymd: cell.ymd, event });
    },

    eventAccess(e) {
      if (this.isCreatedByMe(e)) return "participate";

      const groupIds = Array.isArray(e?.groupIds) ? e.groupIds : [];
      const shareByGroup =
        e?.shareByGroup && typeof e.shareByGroup === "object"
          ? e.shareByGroup
          : {};
      const accessByGroup =
        e?.accessByGroup && typeof e.accessByGroup === "object"
          ? e.accessByGroup
          : {};

      if (groupIds.length) {
        const modes = groupIds
          .map((gid) => {
            const id = String(gid);
            return shareByGroup[id] || accessByGroup[id];
          })
          .filter((mode) => mode === "view" || mode === "participate");

        if (modes.includes("participate")) return "participate";
        if (modes.includes("view")) return "view";
      }

      const a = e?.access?.access || e?.access;
      return a === "view" ? "view" : "participate";
    },

    eventAccessSummary(e) {
      if (this.isCreatedByMe(e)) return "participate";

      const groupIds = Array.isArray(e?.groupIds) ? e.groupIds : [];
      const shareByGroup =
        e?.shareByGroup && typeof e.shareByGroup === "object"
          ? e.shareByGroup
          : {};
      const accessByGroup =
        e?.accessByGroup && typeof e.accessByGroup === "object"
          ? e.accessByGroup
          : {};

      if (groupIds.length) {
        const modes = groupIds
          .map((gid) => {
            const id = String(gid);
            return shareByGroup[id] || accessByGroup[id];
          })
          .filter((mode) => mode === "view" || mode === "participate");
        const hasParticipate = modes.includes("participate");
        const hasView = modes.includes("view");

        if (hasParticipate && hasView) return "mixed";
        if (hasView) return "view";
        if (hasParticipate) return "participate";
      }

      return this.eventAccess(e);
    },

    isCreatedByMe(e) {
      return !!this.uid && !!e?.createdBy && e.createdBy === this.uid;
    },

    hasGroups(e) {
      return Array.isArray(e?.groupIds) && e.groupIds.length > 0;
    },

    eventTone(e) {
      if (this.eventAccess(e) === "view") return "view";
      return this.isCreatedByMe(e) ? "mine" : "participate";
    },

    pillClass(e) {
      const tone = this.eventTone(e);
      if (tone === "view") return "pill-view";
      if (tone === "mine") return "pill-mine";
      return "pill-participate";
    },

    pillStyle(e) {
      const primary = this.$vuetify.theme.currentTheme.primary;
      const tone = this.eventTone(e);

      if (tone === "mine") {
        return {
          "--pillBg": "#a57ce6",
          "--pillBorder": "#a57ce6",
          "--pillText": "#ffffff",
          "--pillIcon": "#ffffff"
        };
      }

      if (tone === "participate") {
        return {
          "--pillBg": "rgba(167, 139, 250, 0.24)",
          "--pillBorder": "rgba(167, 139, 250, 0.62)",
          "--pillText": "#4C1D95",
          "--pillIcon": "#7C3AED"
        };
      }

      return {
        "--pillBg": "rgba(0,0,0,0.055)",
        "--pillBorder": "rgba(0,0,0,0.18)",
        "--pillText": "rgba(0,0,0,0.74)",
        "--pillIcon": "rgba(0,0,0,0.54)"
      };
    },

    tooltipForEvent(e) {
      const s = e?.startAt ? toDate(e.startAt) : null;
      const summary = this.eventAccessSummary(e);
      const access =
        summary === "mixed"
          ? this.$t("calendar.mixedAccess")
          : summary === "view"
            ? this.$t("calendar.viewing")
            : this.$t("calendar.participating");
      if (!s) return `${e?.title || e?.name || ""} · ${access}`;
      return `${e.title || e.name} · ${access} · ${s.toLocaleString(this.$i18n.locale)}`;
    },

    eventKey(e, idx, ymd) {
      return e?.id || `${ymd}-${idx}-${toDate(e.startAt).getTime()}`;
    }
  }
};
</script>

<style scoped>
.k-cal {
  width: 100%;
}

/* Header */
.k-cal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}
.k-cal-title {
  font-weight: 600;
  font-size: 14px;
  text-transform: capitalize;
}

/* Weekdays */
.k-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  margin-bottom: 6px;
}
.k-weekday {
  text-align: center;
  font-size: 11px;
  opacity: 0.6;
  font-weight: 500;
  padding: 4px 0;
}

/* Grid 7x6 */
.k-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  border-left: 1px solid rgba(0, 0, 0, 0.08);
}

/* Celda */
.k-cell {
  border-radius: 0;
  min-height: 118px; /* un pelín más alto desktop */
  cursor: pointer;
  background: #ffffff;
  border-right: 1px solid rgba(0, 0, 0, 0.08);
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  padding: 0;

  /* mantenemos orden visual: no desbordar fuera de la celda */
  overflow: hidden;
}

.k-cell.is-outside {
  opacity: 0.35;
}
.k-cell.is-today .k-day-num {
  background: rgba(0, 0, 0, 0.06);
  border-radius: 999px;
  padding: 2px 8px;
}
.k-cell.is-selected {
  outline: 2px solid rgba(98, 0, 238, 0.25);
}

/* Day num */
.k-cell-top {
  display: flex;
}
.k-day-num {
  font-weight: 700;
  font-size: 15px;
  line-height: 1;
}

/* Events */
.k-cell-events {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;

  /* que use el espacio */
  flex: 1;
}

/* Pill base */
.k-pill {
  border-radius: 8px;
  padding: 4px 6px;
  font-size: 12px;
  line-height: 1.15;

  background-color: var(--pillBg);
  border: 1px solid var(--pillBorder);
  color: var(--pillText);

  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  gap: 3px;
}

/* Desktop: 2 líneas */
.k-pill-text {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  white-space: normal;
  word-break: break-word;
}

.k-pill-group-icon.v-icon {
  color: var(--pillIcon);
  flex: 0 0 auto;
  font-size: 9px;
  height: 11px;
  line-height: 1;
  margin-top: 1px;
  width: 10px;
}

.pill-participate {
  font-weight: 400;
}
.pill-mine {
  font-weight: 400;
}
.pill-view {
  font-weight: 400;
}
.pill-participate::before {
  display: none;
}

.k-more {
  font-size: 8px;
  font-weight: 400;
  opacity: 0.7;
  padding-left: 2px;
}

/* Acciones */
.k-actions {
  display: flex;
  justify-content: flex-start;
  margin-top: 6px;
}

/* ===== MÓVIL ===== */
@media (max-width: 600px) {
  .k-grid {
    gap: 0;
  }

  .k-cell {
    border-radius: 0;
    padding-top: 4px;

    /* CLAVE: si quieres leer, hay que dar altura */
    min-height: 70px;

    gap: 4px;
  }

  .k-day-num {
    font-size: 13px;
  }

  .k-pill {
    /* menos padding horizontal = más letras */
    padding: 1px 2px;
    font-size: 10px;
    line-height: 1.15;
    border-radius: 2px;
    gap: 1px;
  }

  /* CLAVE: en móvil también 2 líneas (así no se queda en "Po...") */
  .k-pill-text {
    -webkit-line-clamp: 1;
    font-size: 1.1vh;
  }

  .k-pill-group-icon.v-icon {
    font-size: 7px;
    height: 8px;
    margin-top: 1px;
    width: 7px;
  }

  .k-more {
    font-size: 10px;
  }

  .k-actions {
    margin-top: 4px;
  }
}
</style>
